import { useCallback, useEffect, useState } from 'react';

import type { NavItemConfigInternal } from '../../../types';

interface ExpandOpenState {
	isExpandable: boolean;
	isExpandOrFlyoutOpen: boolean;
	onExpandFlyoutToggle: (isExpandedOrOpen: boolean) => void;
}

/**
 * Allows managing expands and flyouts open state so they can collapse during drag.
 * The consuming apps may also want to control this state. So, we track the open state here and also listen for
 * the corresponding props so we can update our internal state if the app is controlling it.
 */
export function useExpandOpenState(item: NavItemConfigInternal, peek: boolean): ExpandOpenState {
	const { type } = item;

	const isExpandable = ['expand', 'flyout', 'object-list-flyout'].includes(type);
	const isExpandableOnPeek = ['expand', 'flyout'].includes(type);

	const isExpandedFromProps =
		(type === 'expand' ? item.isExpanded : undefined) ??
		(type === 'flyout' || type === 'object-list-flyout' ? item.isOpen : undefined);

	const isDefaultExpanded =
		(type === 'expand' ? item.isDefaultExpanded : undefined) ??
		(type === 'flyout' || type === 'object-list-flyout' ? item.isDefaultOpen : undefined);

	// Prioritize controlled props if available, otherwise use default expanded/open state
	const initState =
		typeof isExpandedFromProps === 'boolean'
			? isExpandedFromProps
			: typeof isDefaultExpanded === 'boolean'
				? isDefaultExpanded
				: false;

	const [isExpandOrFlyoutOpen, setIsExpandOrFlyoutOpen] = useState(initState);

	// Sync internal state when controlled props change, but not when isDefaultExpanded changes
	useEffect(() => {
		if (!isExpandable) {
			return;
		}
		if (typeof isExpandedFromProps === 'boolean' && !peek) {
			setIsExpandOrFlyoutOpen(isExpandedFromProps);
		}
		if (isExpandableOnPeek && peek) {
			setIsExpandOrFlyoutOpen(true);
		}
	}, [isExpandedFromProps, isExpandable, peek, isExpandableOnPeek]);

	const onExpanded =
		(type === 'expand' ? item.onExpansionToggle : undefined) ??
		(type === 'flyout' || type === 'object-list-flyout' ? item.onOpenChange : undefined);

	const onExpandFlyoutToggle = useCallback(
		(isExpandedOrOpen: boolean) => {
			if (!isExpandable) {
				return;
			}

			setIsExpandOrFlyoutOpen(isExpandedOrOpen);
			onExpanded?.(isExpandedOrOpen);
		},
		[isExpandable, onExpanded],
	);

	return {
		isExpandOrFlyoutOpen,
		onExpandFlyoutToggle,
		isExpandable,
	};
}
